import React, { useState, useEffect, useContext, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { loader } from 'graphql.macro';
import { useQuery, useMutation } from 'react-apollo-hooks';

import { withGraphQLErrors, ProfileContext } from '@loyalup/core';
import { WithTitle, Button, Field, Input, Form, Toggle, Table, Modal, useNotifications } from '@loyalup/ui';

import onlyData from 'utils/onlyData';
import updateProfileInCache from 'utils/updateProfileInCache';

import { ReactComponent as CreateIcon } from 'icons/create.svg';
import { ReactComponent as LockIcon } from 'icons/lock.svg';
import { ReactComponent as UnlockIcon } from 'icons/unlock.svg';

import s from './Company.module.css';

const COMPANY_QUERY = loader('../../../gql/queries/Company.gql');
const COMPANY_USERS_QUERY = loader('../../../gql/queries/CompanyUsers.gql');
const UPDATE_COMPANY_NAME_MUTATION = loader('../../../gql/mutations/UpdateCompanyData.gql');
const SAVE_COMPANY_USER_MUTATION = loader('../../../gql/mutations/SaveCompanyUser.gql');

const ConfirmDialog = ({ user, isOpen, onSubmit, loading, onCancel }) => {
  const [t] = useTranslation();

  const toBan = user.status === 'ACTIVE';

  const title = t('Вы уверены, что хотите {{action}} пользователя {{name}}?', {
    name: user.name,
    action: toBan ? t('заблокировать') : t('разблокировать'),
  });

  return (
    <Modal confirm isOpen={isOpen} onClose={onCancel}>
      <Form onSubmit={onSubmit}>
        <p>{title}</p>
        <div>
          <Button type="submit" fixedWidth={150} center loading={loading}>
            {t(toBan ? t('Заблокировать') : t('Разблокировать'))}
          </Button>
          <Button onClick={onCancel} theme="black">
            {t('Отмена')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

const PermissionsRow = ({ label, checked, onChange }) => (
  <Toggle
    className={cn(s.modalPermissionsRow, { [s.inactivePermission]: !checked })}
    label={label}
    checked={checked}
    onChange={onChange}
  />
);

const MAX_ROWS = 10000;

const Company = () => {
  const [t] = useTranslation();
  const [sendNotification] = useNotifications();

  const { loading, data, refetch } = useQuery(COMPANY_USERS_QUERY, {
    suspend: false,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const { data: companyData } = useQuery(COMPANY_QUERY, {
    suspend: false,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (companyData) {
      const { name, websiteUrl, agreementUrl } = companyData.company;
      setCompanyForm({ name, websiteUrl, agreementUrl });
    }
  }, [companyData]);

  const [companyForm, setCompanyForm] = useState({
    name: '',
    websiteUrl: '',
    agreementUrl: '',
  });

  const { company } = useContext(ProfileContext);

  const [updateCompanyProfile, { loading: updateLoading }] = useMutation(UPDATE_COMPANY_NAME_MUTATION);

  const updateCompanyData = field =>
    updateCompanyProfile({
      variables: field,
      update: (cache, { data: { updateCompanyProfile } }) => {
        const { name, websiteUrl, agreementUrl } = updateCompanyProfile;
        sendNotification({ text: t('Сохранено') });
        updateProfileInCache(cache, { company: { ...company, name, websiteUrl, agreementUrl } });
      },
    });

  const defaultProfile = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    permissions: {
      companyAdmin: true,
      manageCompanyClients: true,
      manageCompanyClientOrders: true,
      manageCompanyCampaigns: true,
      viewCompanyStats: true,
      manageCompanyFeedbacks: true,
      manageCompanyLoyalties: true,
      managePassDesigns: true,
    },
  };

  const reducer = (state, action) => {
    const permissionToDisable = action.payload && !Object.values(action.payload)[0];
    switch (action.type) {
      case 'setFirstName':
        return { ...state, profile: { ...state.profile, firstName: action.firstName } };
      case 'setLastName':
        return { ...state, profile: { ...state.profile, lastName: action.lastName } };
      case 'setEmail':
        return { ...state, profile: { ...state.profile, email: action.email } };
      case 'setPhone':
        return { ...state, profile: { ...state.profile, phone: action.phone } };
      case 'togglePermission':
        if (permissionToDisable) {
          return {
            ...state,
            profile: {
              ...state.profile,
              permissions: {
                ...state.profile.permissions,
                ...action.payload,
                companyAdmin: false,
              },
            },
          };
        }
        return {
          ...state,
          profile: { ...state.profile, permissions: { ...state.profile.permissions, ...action.payload } },
        };
      case 'enableManageCompanyCampaigns':
        return {
          ...state,
          profile: {
            ...state.profile,
            permissions: {
              ...state.profile.permissions,
              manageCompanyClients: true,
              manageCompanyCampaigns: true,
            },
          },
        };
      case 'toggleCompanyAdminPermissions':
        if (permissionToDisable) {
          return {
            ...state,
            profile: {
              ...state.profile,
              permissions: {
                ...state.profile.permissions,
                companyAdmin: false,
              },
            },
          };
        }
        return {
          ...state,
          profile: {
            ...state.profile,
            permissions: {
              companyAdmin: true,
              manageCompanyClients: true,
              manageCompanyClientOrders: true,
              manageCompanyCampaigns: true,
              managePassDesigns: true,
              viewCompanyStats: true,
              manageCompanyLoyalties: true,
              manageCompanyFeedbacks: false,
            },
          },
        };
      case 'setFullProfile':
        return { ...state, profile: action.profile };
      case 'resetProfile':
        return { ...state, profile: null };
      case 'setDefaultProfile':
        return { ...state, profile: defaultProfile };
      case 'setTableConfig':
        return { ...state, tableConfig: action.tableConfig };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    tableConfig: { orderDirection: 'ASC', orderBy: 'email' },
  });

  const [userStatus, setUserStatus] = useState({
    id: null,
    status: null,
    name: null,
  });

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [permissionsError, setPermissionsError] = useState();

  const [saveCompanyUser] = useMutation(SAVE_COMPANY_USER_MUTATION);

  const onSaveCompanyUser = async () => {
    setSubmitLoading(true);

    const hasNoPermission = !Object.values(state.profile.permissions).filter(permission => permission === true).length;

    if (hasNoPermission) {
      setPermissionsError('Необходимо указать права');
      setSubmitLoading(false);

      return;
    }

    const fields = { ...state.profile, status: state.profile.status || 'ACTIVE' };

    const { errors } = await withGraphQLErrors(() => saveCompanyUser({ variables: fields }));

    setSubmitLoading(false);

    if (errors) {
      setErrors(errors);
    } else {
      sendNotification({ text: t('Сохранено') });
      refetch();
      dispatch({ type: 'resetProfile' });
    }
  };

  const translations = {
    manageCompanyClients: t('Управление клиентами'),
    manageCompanyClientOrders: t('Управление заказами'),
    manageCompanyCampaigns: t('Управление рассылками'),
    viewCompanyStats: t('Просмотр аналитики'),
    companyAdmin: t('Полный доступ'),
    managePassDesigns: t('Доступ к дизайнеру карт'),
    manageCompanyLoyalties: t('Доступ к программе лояльности'),
  };

  const changeStatusForUser = ({ id, status }) => {
    saveCompanyUser({
      variables: {
        id,
        status: status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE',
      },
    });
    refetch();
  };

  const statusText = {
    ACTIVE: t('Активный'),
    DISABLED: t('Неактивный'),
  };

  const columns = [
    {
      label: t('Е-мейл'),
      name: 'email',
      type: 'email',
      render: ({ email, isMe }) => email + (isMe ? ` (${t('Вы')})` : ''),
    },
    {
      label: t('Имя'),
      name: 'firstName',
      type: 'text',
      render: ({ firstName }) => firstName,
    },
    {
      label: t('Фамилия'),
      name: 'lastName',
      type: 'text',
      render: ({ lastName }) => lastName,
    },
    {
      label: t('Телефон'),
      name: 'phone',
      type: 'phone',
      render: ({ phone }) => phone,
    },
    {
      label: t('Права'),
      name: 'permissions',
      type: 'permissions',
      render: ({ permissions }) => (
        <span className={s.permissionsColumn}>
          {permissions.companyAdmin ? <b>{translations.companyAdmin}: </b> : ''}
          {Object.keys(permissions)
            .filter(key => key !== 'companyAdmin' && translations.hasOwnProperty(key) && !!permissions[key])
            .map(key => translations[key])
            .join(', ')}
        </span>
      ),
    },
    {
      label: t('Статус'),
      name: 'status',
      type: 'status',
      render: ({ status, id, isMe, firstName, lastName }) => {
        return (
          <span
            onClick={
              !isMe
                ? () => {
                    setUserStatus({ status, id, name: `${firstName} ${lastName}` });
                    setIsConfirmModalOpen(true);
                  }
                : null
            }
            className={cn(
              {
                [s.activeAccountType]: status === 'ACTIVE',
                [s.inactiveAccoutType]: status === 'DISABLED',
                [s.disabledIcon]: isMe,
              },
              s.accountType,
              !isMe && s.touchable,
            )}
          >
            {status === 'ACTIVE' ? <UnlockIcon /> : <LockIcon />}
            <span className={s.statusText}>{statusText[status]}</span>
          </span>
        );
      },
    },
  ];

  const manageUsers = ({ isMe, id, firstName, lastName, email, phone, permissions }) => {
    if (isMe) return;

    dispatch({
      type: 'setFullProfile',
      profile: {
        firstName,
        lastName,
        email,
        phone: phone || '',
        permissions: onlyData(permissions),
        id,
      },
    });
  };

  const onCancelModal = () => {
    dispatch({ type: 'resetProfile' });
    setErrors({});
  };

  const getRows = () => {
    if (!Array.isArray(data && data.companyUsers)) {
      return [];
    }

    return data.companyUsers.map(user => ({
      ...user,
      isInactive: user.isMe,
    }));
  };

  return (
    <WithTitle title={t('Компания')}>
      <div className={s.header}>
        <h1>{t('Компания')}</h1>
        <Button
          theme="green"
          icons="first"
          onClick={() => dispatch({ type: 'setDefaultProfile' })}
          className={s.addEmployeeButton}
        >
          <CreateIcon />
          {t('Добавить сотрудника')}
        </Button>
      </div>
      <main className={s.main}>
        <Form
          onSubmit={() => updateCompanyData(companyForm)}
          layout="horizontal"
          className={s.updateCompanyDataForm}
          noContainers
        >
          <label>
            {t('Название компании')}
            <Input
              className={s.input}
              required
              value={companyForm.name}
              onChange={name => setCompanyForm(prev => ({ ...prev, name }))}
              theme="toolbar-white"
            />
          </label>
          <label>
            {t('Сайт компании')}
            <Input
              className={s.input}
              value={companyForm.websiteUrl}
              onChange={websiteUrl => setCompanyForm(prev => ({ ...prev, websiteUrl }))}
              theme="toolbar-white"
            />
          </label>
          <label>
            {t('Лицензионное соглашение')}
            <Input
              className={s.input}
              value={companyForm.agreementUrl}
              onChange={agreementUrl => setCompanyForm(prev => ({ ...prev, agreementUrl }))}
              theme="toolbar-white"
            />
          </label>
          <Button
            loading={updateLoading}
            className={s.button}
            disabled={!companyForm.name}
            type="submit"
            fixedWidth={160}
            center
          >
            {t('Сохранить')}
          </Button>
        </Form>
        <h1>{t('Сотрудники')}</h1>
        <Table
          loading={loading}
          selectable={false}
          selection={null}
          total={MAX_ROWS}
          tableConfig={state.tableConfig}
          columns={columns}
          rows={getRows()}
          onRowDoubleClick={manageUsers}
          onChange={tableConfig => dispatch({ type: 'setTableConfig', tableConfig })}
          localSort
        />

        <ConfirmDialog
          isOpen={isConfirmModalOpen}
          user={userStatus}
          onSubmit={() => {
            setIsConfirmModalOpen(false);
            changeStatusForUser(userStatus);
          }}
          onCancel={() => setIsConfirmModalOpen(false)}
        />

        <Modal isOpen={!!state.profile} title={t('Добавление сотрудника')} onClose={onCancelModal}>
          <Form onSubmit={onSaveCompanyUser}>
            <div className={s.row}>
              <Field
                value={state.profile ? state.profile.firstName : ''}
                onChange={firstName => dispatch({ type: 'setFirstName', firstName })}
                label={t('Имя')}
                type="text"
                errors={errors.firstName}
                autoFocus
                required
              />
              <Field
                value={state.profile ? state.profile.lastName : ''}
                onChange={lastName => dispatch({ type: 'setLastName', lastName })}
                className={s.lastName}
                label={t('Фамилия')}
                type="text"
                errors={errors.lastName}
                required
              />
            </div>
            <Field
              value={state.profile ? state.profile.email : ''}
              onChange={email => dispatch({ type: 'setEmail', email })}
              label={t('Е-мейл')}
              type="text"
              errors={errors.email}
              required
            />
            <Field
              value={state.profile ? state.profile.phone : ''}
              onChange={phone => dispatch({ type: 'setPhone', phone })}
              label={t('Телефон')}
              type="phone"
              error={errors.phone}
            />
            <b className={s.permissionsTitle}>{t('Права')}</b>
            <PermissionsRow
              checked={!!state.profile?.permissions.manageCompanyClients}
              onChange={manageCompanyClients =>
                dispatch({ type: 'togglePermission', payload: { manageCompanyClients } })
              }
              label={translations.manageCompanyClients}
            />
            <PermissionsRow
              checked={!!state.profile?.permissions.manageCompanyClientOrders}
              onChange={manageCompanyClientOrders =>
                dispatch({ type: 'togglePermission', payload: { manageCompanyClientOrders } })
              }
              label={translations.manageCompanyClientOrders}
            />
            <PermissionsRow
              checked={!!state.profile?.permissions.manageCompanyCampaigns}
              onChange={enabled => {
                if (enabled) {
                  dispatch({ type: 'enableManageCompanyCampaigns' });
                } else {
                  dispatch({ type: 'togglePermission', payload: { manageCompanyCampaigns: enabled } });
                }
              }}
              label={translations.manageCompanyCampaigns}
            />
            <PermissionsRow
              checked={!!state.profile?.permissions.viewCompanyStats}
              onChange={viewCompanyStats => dispatch({ type: 'togglePermission', payload: { viewCompanyStats } })}
              label={translations.viewCompanyStats}
            />
            <PermissionsRow
              checked={!!state.profile?.permissions.manageCompanyLoyalties}
              onChange={manageCompanyLoyalties =>
                dispatch({ type: 'togglePermission', payload: { manageCompanyLoyalties } })
              }
              label={translations.manageCompanyLoyalties}
            />
            <PermissionsRow
              checked={!!state.profile?.permissions.managePassDesigns}
              onChange={managePassDesigns => dispatch({ type: 'togglePermission', payload: { managePassDesigns } })}
              label={translations.managePassDesigns}
            />
            <PermissionsRow
              checked={!!state.profile?.permissions.companyAdmin}
              onChange={companyAdmin => dispatch({ type: 'toggleCompanyAdminPermissions', payload: { companyAdmin } })}
              label={translations.companyAdmin}
            />
            {permissionsError && <span className={s.error}>{permissionsError}</span>}
            <div className={cn(s.row, s.modalButtons)}>
              <Button fixedWidth={150} center type="submit" loading={submitLoading}>
                {t('Сохранить')}
              </Button>
              <Button onClick={onCancelModal} theme="black">
                {t('Отмена')}
              </Button>
            </div>
          </Form>
        </Modal>
      </main>
    </WithTitle>
  );
};

export default Company;
